import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import { sendPasswordReset } from '../lib/auth';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await sendPasswordReset(email);
      setSent(true);
    } catch (err) {
      // We deliberately don't show 'no account with that email' style errors
      // — that would let an attacker enumerate accounts. Show success either
      // way and rely on the email never arriving for invalid addresses.
      console.warn('[ForgotPassword] reset error', err);
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout title="Check your inbox">
        <h1>Check your inbox</h1>
        <p className="muted">
          If an account exists for <strong>{email}</strong>, we've sent a
          link to reset your password. It'll arrive in a minute or two.
        </p>
        <p className="muted" style={{ fontSize: 13, marginTop: 16 }}>
          Tap the link in that email to set a new password. You can close
          this tab.
        </p>
        <p className="auth-footer" style={{ marginTop: 24 }}>
          Didn't get it? Check spam, then{' '}
          <Link to="/forgot-password" onClick={() => setSent(false)}>try again</Link>.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Forgotten password">
      <h1>Forgotten password?</h1>
      <p className="muted">
        Enter the email you signed up with — we'll send you a link to set
        a new one.
      </p>

      {error && <div className="banner banner-error">{error}</div>}

      <form onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="fp-email">Email</label>
          <input
            id="fp-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            enterKeyHint="send"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      <p className="auth-footer">
        Remembered it? <Link to="/login">Back to sign in</Link>
      </p>
    </AuthLayout>
  );
}
